<template>
  <div v-loading="loading">
    <div class="side-highlight-container">
      <BaseElForm ref="formRef" label-position="top" :model="formData" :rules="formRules">
        <BaseElFormItem :label="displayText.name" prop="name">
          <BaseElInput v-model="formData.name" />
        </BaseElFormItem>
        <BaseElFormItem :label="displayText.type" prop="type">
          <BaseElSelect v-model="formData.type">
            <BaseElSelectOption
              v-for="option in dynamicTargetTypeConfig"
              :key="option.value"
              :value="option.value"
              :label="option.label"
            />
          </BaseElSelect>
        </BaseElFormItem>

        <!-- 預約類型的配置區塊 -->
        <GrayBlockContainer v-if="formData.type === 'appointment'">
          <div>
            <div class="flex">
              {{ displayText.selectTarget.title }} <span>( {{ displayText.selectTarget.desc }} )</span>
              <TipInfo v-if="showControl.riskLevelOption" width="200">
                <div v-html="displayText.selectTarget.tooltip" />
              </TipInfo>
            </div>
            <BaseElFormItem :label="displayText.service" prop="servicesHolder">
              <template slot="label">
                <div class="flex items-center">
                  {{ displayText.service }}
                  <TipInfo width="300" class="ml-1">
                    <div v-html="displayText.serviceTip" />
                  </TipInfo>
                </div>
              </template>
              <BaseElSelect
                v-model="formData.servicesHolder"
                clearable
                multiple
                collapse-tags
                :empty-text="displayText.emptyText"
                popper-class="select-popper"
                value-key="id"
                @visible-change="showSelector.service = true"
                @change="$emit('update')"
                @clear="onCategoryServiceClear"
              >
                <BaseElSelectOption
                  v-for="service in formData.servicesHolder"
                  :key="service.id"
                  :label="service.name"
                  :value="service"
                />
              </BaseElSelect>
            </BaseElFormItem>
            <BaseElFormItem v-if="useSubService" :label="displayText.subService">
              <BaseElSelect
                v-model="formData.subServicesHolder"
                clearable
                multiple
                collapse-tags
                :empty-text="displayText.emptyText"
                popper-class="select-popper"
                value-key="id"
                @visible-change="showSelector.subService = true"
                @change="$emit('update')"
                @clear="formData.subServicesHolder = []"
              >
                <BaseElSelectOption
                  v-for="service in formData.subServicesHolder"
                  :key="service.id"
                  :label="service.name"
                  :value="service"
                />
              </BaseElSelect>
            </BaseElFormItem>
            <BaseElFormItem :label="displayText.attachService">
              <BaseElSelect
                v-model="formData.attachServicesHolder"
                clearable
                multiple
                collapse-tags
                :empty-text="displayText.emptyText"
                popper-class="select-popper"
                value-key="id"
                @visible-change="showSelector.attachService = true"
                @change="$emit('update')"
                @clear="formData.attachServicesHolder = []"
              >
                <BaseElSelectOption
                  v-for="service in formData.attachServicesHolder"
                  :key="service.id"
                  :label="service.name"
                  :value="service"
                />
              </BaseElSelect>
            </BaseElFormItem>

            <!-- 風險層級選項-->
            <div v-if="showControl.riskLevelOption" class="mt-4">
              <el-divider>
                <Tag type="primary" size="small" disable-transitions>and</Tag>
              </el-divider>
              <BaseElFormItem :label="displayText.riskLevel" prop="riskLevelsHolder">
                <template slot="label">
                  <div class="flex items-center">
                    {{ displayText.riskLevel }}
                    <TipInfo width="300" class="ml-1">
                      <div v-html="displayText.riskLevelTip" />
                    </TipInfo>
                  </div>
                </template>
                <BaseElSelect
                  v-model="formData.riskLevelsHolder"
                  clearable
                  multiple
                  collapse-tags
                  :empty-text="displayText.emptyText"
                  popper-class="select-popper"
                  value-key="value"
                  @visible-change="showSelector.riskLevel = true"
                  @change="$emit('update')"
                  @clear="formData.riskLevelsHolder = []"
                >
                  <BaseElSelectOption
                    v-for="level in formData.riskLevelsHolder"
                    :key="level.value"
                    :label="level.label"
                    :value="level"
                  />
                </BaseElSelect>
              </BaseElFormItem>
            </div>
          </div>
        </GrayBlockContainer>
      </BaseElForm>
    </div>

    <CategoryServiceAdvanceSelector
      v-if="showSelector.service"
      :data="categoryServiceDataList"
      :syncCompose="syncComposeServiceData"
      @close="showSelector.service = false"
      @confirm="formData.servicesHolder = $event"
      @compose="onCategoryServiceCompose"
    />
    <SubServiceAdvanceSelector
      v-if="showSelector.subService"
      :data="serviceDataList"
      :syncData="formData.subServicesHolder"
      @close="showSelector.subService = false"
      @confirm="formData.subServicesHolder = $event"
    />
    <AttachServiceAdvanceSelector
      v-if="showSelector.attachService"
      :data="attachServiceDataList"
      :syncData="formData.attachServicesHolder"
      @close="showSelector.attachService = false"
      @confirm="formData.attachServicesHolder = $event"
    />
    <RiskLevelAdvanceSelector
      v-if="showSelector.riskLevel"
      :syncData="formData.riskLevelsHolder"
      @close="showSelector.riskLevel = false"
      @confirm="formData.riskLevelsHolder = $event"
    />

    <PageFixedFooter
      :confirmLoading="loading"
      @cancel="onCancel"
      @confirm="onSubmit"
    />
  </div>
</template>

<script>
import { defineComponent, computed, onMounted, reactive, set, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { targetTypeConfig } from '@/config/progressNotification'
import { appointmentRiskGuardConfig } from '@/config/appointmentRiskGuard'
// components
import PageFixedFooter from '@/components/Footer/PageFixedFooter.vue'
import GrayBlockContainer from '@/components/Container/GrayBlockContainer.vue'
import SubServiceAdvanceSelector from '@/components/Select/SubServiceAdvanceSelector.vue'
import CategoryServiceAdvanceSelector from '@/components/Select/CategoryServiceAdvanceSelector.vue'
import AttachServiceAdvanceSelector from '@/components/Select/AttachServiceAdvanceSelector.vue'
import RiskLevelAdvanceSelector from './components/RiskLevelAdvanceSelector.vue'
import TipInfo from '@/components/TipInfo.vue'
// api
import {
  FindProgressNotificationTargetTemplate,
  CreateProgressNotificationTargetTemplate,
  UpdateProgressNotificationTargetTemplate,
  GetNotifyTargetTemplateAppointmentConfigOptions,
} from '@/api/progressNotification'
// utils
import { noEmptyRules } from '@/validation'
import { get, map, isEmpty, find, filter, flatten, uniq } from 'lodash'
// use
import { useBaseForm } from '@/use/useForm'
import { useFetch } from '@/use/fetch'
import { useShop } from '@/use/shop'
import { usePermissions } from '@/use/permissions'

export default defineComponent({
  name: 'NotificationTargetTemplateEdit',
  components: { PageFixedFooter, GrayBlockContainer, TipInfo, CategoryServiceAdvanceSelector, SubServiceAdvanceSelector, AttachServiceAdvanceSelector, RiskLevelAdvanceSelector },
  // emits: ['refresh', 'close'],
  setup (props, { emit }) {
    const router = useRouter()
    const route = useRoute()
    const { shopId } = useShop()
    const { simpleFetch } = useFetch()
    const { initFormData, formData, formRef, loading, formSubmit } = useBaseForm()
    const { checkAction } = usePermissions()

    const useSubService = computed(() => checkAction('admin.appointmentSubService.edit'))
    const showSelector = reactive({
      service: false,
      subService: false,
      attachService: false,
      riskLevel: false,
    })
    const filterRemovedServices = (dataList) => {
      return filter(uniq(dataList), service => {
        if (service.type === 'service' && service.category) return service
        else if (service.type === 'category') return service
      })
    }
    const syncComposeServiceData = computed(() => {
      return {
        activeCategorys: filterRemovedServices([...formData.serviceSettings.categoryIds]),
        activeServices: filterRemovedServices([...formData.serviceSettings.includeServiceIds]),
        deActiveServices: filterRemovedServices([...formData.serviceSettings.excludeServiceIds]),
      }
    })

    const targetId = computed(() => get(route.params, 'id'))

    const showControl = computed(() => {
      const controls = {
        riskLevelOption: checkAction('planModule.appointmentRiskGuard.enable'),
      }
      return controls
    })

    // 動態調整 targetTypeConfig
    const dynamicTargetTypeConfig = computed(() => {
      const config = { ...targetTypeConfig }
      if (showControl.value.riskLevelOption) {
        config.appointment = {
          ...config.appointment,
          label: '預約訂單',
        }
      }
      return config
    })

    const displayText = computed(() => {
      const data = {
        name: '範本名稱',
        type: '通知對象類型',
        selectTarget: {
          title: '選擇通知對象',
          desc: '下列對象均會收到通知',
          tooltip: '<b>通知對象條件說明：</b><br><br><li>下方的條件至少需選擇「服務項目」或「訂單風險」其中之一，也可以同時選擇兩者。</li><li>同一欄位內為「或」(OR) 關係：例如服務項目選擇 A 與 B，則符合 A 或 B 的訂單都會被包含。</li><li>不同欄位之間為「且」(AND) 關係：例如服務項目選擇 A 或 B，且訂單風險為「高」，只有同時符合的訂單才會被包含。</li>',
        },
        service: '服務項目',
        serviceTip: '您所選擇的所有項目選項，只要滿足任何一個項目，就會收到通知。<br><br>舉例：您設定了服務項目A、服務項目B、附加服務C。只要客人預約任何一個項目即可收到通知，無論是否有加選附加服務。因此建議同類型服務項目間可忽略附加服務的選擇。<br><br>欲針對特定附加服務發送通知，請單獨設置對應的範本。',
        subService: '子項目',
        attachService: '附加服務',
        riskLevel: '訂單風險',
        riskLevelTip: '您所選擇的所有訂單風險，只要滿足任何一個項目，就會收到通知。<br><br>舉例：您設定了風險層級「高」和「中」。只要客人符合任一風險層級即可收到通知。',
        emptyText: '暫無數據',
      }
      return data
    })
    initFormData({
      name: null,
      type: null,
      servicesHolder: [],
      subServicesHolder: [],
      attachServicesHolder: [],
      riskLevelsHolder: [],

      serviceSettings: {
        includeServiceIds: [],
        excludeServiceIds: [],
        categoryIds: [],
      },
      subServiceSettings: {
        subServiceIds: [],
      },
      serviceAttachSettings: {
        serviceAttachIds: [],
      },
      riskLevelConditions: [],
    })

    // 表單欄位規則
    const formRules = computed(() => {
      const rules = {
        name: [noEmptyRules()],
        type: [noEmptyRules()],
      }

      // 如果是 appointment 類型的驗證規則
      if (formData.type === 'appointment') {
        // 如果有開啟風險層級功能，則 servicesHolder 和 riskLevelsHolder 至少選一個
        if (showControl.value.riskLevelOption) {
          // 自定義驗證：至少選一個
          const atLeastOneRule = {
            validator: (rule, value, callback) => {
              const hasServices = !isEmpty(formData.servicesHolder)
              const hasRiskLevels = !isEmpty(formData.riskLevelsHolder)
              if (!hasServices && !hasRiskLevels) {
                callback(new Error('請至少選擇一個服務項目或訂單風險'))
              } else {
                callback()
              }
            },
            trigger: 'change',
          }
          rules.servicesHolder = [atLeastOneRule]
          rules.riskLevelsHolder = [atLeastOneRule]
        } else {
          // 沒有風險層級功能，服務項目必填
          rules.servicesHolder = [noEmptyRules()]
        }
      }

      return rules
    })

    const submitData = computed(() => {
      const data = {
        serviceSettings: null,
        subServiceSettings: null,
        serviceAttachSettings: null,
        riskLevelConditions: null,
      }

      // appointment 類型的資料處理
      if (formData.type === 'appointment') {
        // 服務相關設定
        if (!isEmpty(formData.servicesHolder)) {
          data.serviceSettings = {
            includeServiceIds: uniq(map(formData.serviceSettings.includeServiceIds, 'id')),
            excludeServiceIds: uniq(map(formData.serviceSettings.excludeServiceIds, 'id')),
            categoryIds: uniq(map(formData.serviceSettings.categoryIds, 'id')),
          }
        }
        if (!isEmpty(formData.subServicesHolder)) {
          data.subServiceSettings = {
            subServiceIds: uniq(map(formData.subServicesHolder, 'id')),
          }
        }
        if (!isEmpty(formData.attachServicesHolder)) {
          data.serviceAttachSettings = {
            serviceAttachIds: uniq(map(formData.attachServicesHolder, 'id')),
          }
        }

        // 風險層級設定
        if (!isEmpty(formData.riskLevelsHolder)) {
          data.riskLevelConditions = uniq(map(formData.riskLevelsHolder, 'value'))
        } else {
          delete data.riskLevelConditions
        }
      }

      return {
        shopId: shopId.value,
        id: targetId.value,
        name: formData.name,
        type: formData.type,
        appointmentConfig: data,
      }
    })

    // 同步表單資料
    const syncFormData = (data) => {
      formData.name = data.name
      formData.type = data.type

      const serviceSettings = get(data, 'appointmentConfig.serviceSettings')
      const subServiceSettings = get(data, 'appointmentConfig.subServiceSettings')
      const serviceAttachSettings = get(data, 'appointmentConfig.serviceAttachSettings')
      const riskLevelConditions = get(data, 'appointmentConfig.riskLevelConditions')

      const includeServices = map(get(serviceSettings, 'includeServiceIds'), serviceId => {
        return find(getDataByPath(categoryServiceDataList.value, 'services'), { id: serviceId }) || find(serviceDataList.value, { id: serviceId })
      }).filter(Boolean)
      const excludeServices = map(get(serviceSettings, 'excludeServiceIds'), serviceId => {
        return find(getDataByPath(categoryServiceDataList.value, 'services'), { id: serviceId })
      }).filter(Boolean)
      const category = map(get(serviceSettings, 'categoryIds'), serviceId => {
        return find(categoryServiceDataList.value, { id: serviceId })
      }).filter(Boolean)
      const subServices = map(get(subServiceSettings, 'subServiceIds'), serviceId => {
        return find(getDataByPath(serviceDataList.value, 'subServices'), { id: serviceId })
      }).filter(Boolean)
      const attachServices = map(get(serviceAttachSettings, 'serviceAttachIds'), serviceId => {
        return find(attachServiceDataList.value, { id: serviceId })
      }).filter(Boolean)
      const riskLevels = map(riskLevelConditions, riskLevel => {
        return appointmentRiskGuardConfig.riskLevelOptions.find(option => option.value === riskLevel)
      }).filter(Boolean)

      // holder
      formData.servicesHolder = includeServices
      formData.subServicesHolder = subServices
      formData.attachServicesHolder = attachServices
      // category service
      formData.serviceSettings.includeServiceIds = includeServices
      formData.serviceSettings.excludeServiceIds = excludeServices
      formData.serviceSettings.categoryIds = category
      // sub service
      formData.subServiceSettings.subServiceIds = subServices
      // attach service
      formData.serviceAttachSettings.serviceAttachIds = attachServices
      // risk level
      formData.riskLevelsHolder = riskLevels
    }

    // 清空服務選擇
    const onCategoryServiceClear = () => {
      set(formData, 'serviceSettings', {
        includeServiceIds: [],
        excludeServiceIds: [],
        categoryIds: [],
      })
    }

    // 組合服務資料
    const onCategoryServiceCompose = (composeData) => {
      set(formData.serviceSettings, 'includeServiceIds', composeData.serviceActive)
      set(formData.serviceSettings, 'excludeServiceIds', composeData.serviceDeActive)
      set(formData.serviceSettings, 'categoryIds', composeData.categoryActive)
    }

    const onCancel = () => router.push({ name: 'ProgressNotificationTargetTemplateList' })

    // 送出表單
    const onSubmit = async () => {
      const handler = async (data, { emit, loading }) => {
        let apiMethod
        if (!targetId.value) apiMethod = CreateProgressNotificationTargetTemplate
        if (targetId.value) apiMethod = UpdateProgressNotificationTargetTemplate
        const [, err] = await apiMethod(data)
        loading.value = false
        if (err) {
          window.$message.error(err)
          // return
        }
        router.push({ name: 'ProgressNotificationTargetTemplateList' })
        // emit('refresh')
        // emit('close')
      }
      await formSubmit(handler, submitData.value, { emit, loading })
    }

    const reservationServicesData = ref({})
    const getDataByPath = (sourceData, dataPath) => {
      return flatten(map(sourceData, item => get(item, dataPath)))
    }

    const categoryServiceDataList = computed(() => {
      return map(get(reservationServicesData.value, 'appointmentCategories', []), category => ({
        name: category.name,
        id: category.id,
        type: 'category',
        services: map(get(category, 'AppointmentServices'), service => ({
          category: category.id,
          name: service.name,
          id: service.id,
          type: 'service',
        })),
      }))
    })

    const serviceDataList = computed(() => {
      return map(get(reservationServicesData.value, 'appointmentServices', []), service => ({
        name: service.name,
        id: service.id,
        type: 'service',
        subServices: map(get(service, 'AppointmentSubServices'), subService => ({
          service: service.id,
          name: subService.name,
          id: subService.id,
          type: 'sub-service',
        })),
      }))
    })

    const attachServiceDataList = computed(() => {
      return map(get(reservationServicesData.value, 'appointmentServiceAttaches', []), service => ({
        name: service.name,
        id: service.id,
        // type: 'service',
      }))
    })

    onMounted(async () => {
      await simpleFetch(GetNotifyTargetTemplateAppointmentConfigOptions, { shopId: shopId.value }, (res) => {
        reservationServicesData.value = res
      })
      if (targetId.value) {
        simpleFetch(FindProgressNotificationTargetTemplate, { shopId: shopId.value, id: targetId.value },
          (res) => syncFormData(res),
        )
      }
    })

    return {
      reservationServicesData,
      loading,
      formRef,
      formData,
      formRules,
      onCancel,
      onSubmit,
      showSelector,
      targetTypeConfig,
      showControl,
      onCategoryServiceCompose,
      onCategoryServiceClear,
      submitData,
      categoryServiceDataList,
      serviceDataList,
      attachServiceDataList,
      useSubService,
      syncComposeServiceData,
      displayText,
      dynamicTargetTypeConfig,
    }
  },
})
</script>
<style lang="postcss" scoped>
.el-divider--horizontal {
  max-width: 558px;
}
::v-deep .el-divider__text {
  @apply bg-[#F5F7FA] px-2 pt-2;
}
</style>
